import React from 'react';
import * as format from '../utils/formatters';
import * as sorter from '../utils/sorters';
import classNames from '@app/ui/sports-components/utils/classNames';
import { NAME_ACCESSOR_COLUMNS } from './constants';
import { defaultTotals } from './initialStates';
import ExpandableCampaignTags from './ExpandableCampaignTags';
import ExpandableCampaignTagsV2 from './components/ExpandableCampaignTags';
import Tooltip from '@app/ui/sports-components/Tooltip';
import { getPlatformIconClasses } from './utils';
import { Typography, Flex } from '@app/ui';
import './styles/allSocialMediaPosts.scss';

const IMAGE_TYPE_ID = 1;
type SocialPostsMetricTotals = {
    engagementsTotal: number;
    impressionsTotal: number;
    videoViewsTotal: number;
    fmvTotal: number;
};

type SocialPostTableConfigs = {
    sortedByColumn: any;
};

export default (
    totals: SocialPostsMetricTotals = defaultTotals,
    { sortedByColumn }: SocialPostTableConfigs,
    openModalForPost: (postId: number) => void,
    currency?: string,
    isV2?: boolean,
    t?: (key: string) => string,
) => {
    const getColumnName = (nameAccessorColumn: {
        name: string;
        accessor: string;
    }) => {
        const columnKeyMap: { [key: string]: string } = {
            createdAt: 'socialPostMetrics.columns.date',
            platformId: 'socialPostMetrics.columns.platform',
            mediaTypeId: 'socialPostMetrics.columns.mediaType',
            impressions: 'socialPostMetrics.columns.impressions',
            videoViews: 'socialPostMetrics.columns.videoViews',
            engagements: 'socialPostMetrics.columns.engagements',
            fullMediaValue: 'socialPostMetrics.columns.fmv',
        };
        return t && columnKeyMap[nameAccessorColumn.accessor]
            ? t(columnKeyMap[nameAccessorColumn.accessor])
            : nameAccessorColumn.name;
    };

    const getHeader = nameAccessorColumn => {
        const columnName = t
            ? getColumnName(nameAccessorColumn)
            : nameAccessorColumn.name;

        if (sortedByColumn.name !== nameAccessorColumn.accessor)
            return columnName;

        const sortingIcon = sortedByColumn.desc ? (
            <i className="fas fa-lg fa-caret-down" />
        ) : (
            <i className="fas fa-lg fa-caret-up" />
        );

        return (
            <span>
                {columnName} {sortingIcon}
            </span>
        );
    };

    return [
        !isV2 && {
            Header: '',
            accessor: 'selected',
            Cell: (row: {
                value: string;
                index: number;
                original: {
                    selected: boolean;
                    shouldDisplaySelectionCheckbox: boolean;
                    togglePostSelection: any;
                    isSelectPostCheckboxDisabled: boolean;
                };
            }) => {
                const isChecked = row.original.selected;
                const shouldDisableAllPostSelections =
                    row.original.isSelectPostCheckboxDisabled;
                const shouldDisplaySelectionCheckbox =
                    row.original.shouldDisplaySelectionCheckbox;

                if (!shouldDisplaySelectionCheckbox) return '';

                return (
                    <span>
                        <input
                            type="checkbox"
                            data-test="select-post-checkbox"
                            checked={isChecked}
                            disabled={shouldDisableAllPostSelections}
                            onChange={() => row.original.togglePostSelection()}
                        />
                    </span>
                );
            },
            width: 33,
            headerClassName: '--secondary',
            className: 'post-select-cell',
        },
        {
            Header: t ? t('socialPostMetrics.columns.media') : 'Media',
            accessor: 'cdnMediaUrl',
            // eslint-disable-next-line react/no-multi-comp, react/display-name
            Cell: row => {
                const { value, original } = row;
                let mediaUrl = original.carouselMediaUrls
                    ? original.carouselMediaUrls[0]
                    : value || original.mediaUrl;
                if (!mediaUrl) {
                    return (
                        <p className="no-media-message">
                            {t
                                ? t(
                                      'socialPostMetrics.columns.noMediaAvailable',
                                  )
                                : 'No media available'}
                        </p>
                    );
                }

                if (original.mediaTypeId === IMAGE_TYPE_ID) {
                    return (
                        <img
                            onError={() => (mediaUrl = null)}
                            data-test="post-media-image"
                            className="post-media"
                            onClick={() => openModalForPost(original.id)}
                            alt="post image"
                            style={{ width: '100%' }}
                            src={mediaUrl}
                        />
                    );
                }
                return (
                    <video
                        data-test="post-media-video"
                        className="post-media"
                        onClick={() => openModalForPost(original.id)}
                        style={{ width: '100%' }}
                        src={mediaUrl}
                    />
                );
            },
            textAlign: 'center',
            headerClassName: '--secondary',
            className: 'gds-flex gds-flex--justify-center',
            sortable: false,
            width: 123,
        },
        {
            Header: getHeader(NAME_ACCESSOR_COLUMNS.DATE),
            accessor: 'createdAt',
            format: date => format.shortDate(date),
            textAlign: 'center',
            headerClassName: '--secondary',
            sortMethod: sorter.date,
            Footer: () => {
                return t ? t('socialPostMetrics.columns.total') : 'TOTAL:';
            },
            Cell: ({ value }: { value: string }) => format.shortDate(value),
            sortable: true,
            headerStyle: {
                cursor: 'pointer',
            },
            width: 150,
        },
        {
            Header: getHeader(NAME_ACCESSOR_COLUMNS.PLATFORM),
            accessor: 'platformId',
            textAlign: 'center',
            headerClassName: '--secondary',
            // eslint-disable-next-line react/no-multi-comp
            Cell: ({
                value,
                original,
            }: {
                value: number;
                original: { url: string };
            }) => {
                const socialIconClasses = classNames(
                    'gds-text--body-md',
                    getPlatformIconClasses(value),
                );
                if (isV2) {
                    return (
                        <a
                            href={original.url}
                            className="gds-text--body-md"
                            data-test="platform-link"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <i
                                className={classNames(
                                    socialIconClasses,
                                    'social-icon-colored',
                                )}
                                aria-hidden="true"
                            />
                        </a>
                    );
                }
                return <i className={socialIconClasses} aria-hidden="true" />;
            },
            sortable: true,
            width: 150,
            headerStyle: { cursor: 'pointer' },
        },
        {
            Header: getHeader(NAME_ACCESSOR_COLUMNS.MEDIA_TYPE),
            accessor: 'mediaTypeIds',
            Cell: (row: {
                value: number[];
                original: {
                    mediaTypeId: number;
                    carousel: boolean;
                };
            }) => {
                if (row.original.carousel) {
                    const mediaTypeIds = row.value ?? [];
                    return mediaTypeIds.length > 1
                        ? t
                            ? `${t('filters.mediaType.carousel')} - ${t('filters.mediaType.imagesAndVideos')}`
                            : 'Carousel - Images & Videos'
                        : t
                          ? `${t('filters.mediaType.carousel')} - ${getMediaTypeNameById(row.original.mediaTypeId, t)}s`
                          : `Carousel - ${getMediaTypeNameById(row.original.mediaTypeId)}s`;
                }
                return getMediaTypeNameById(row.original.mediaTypeId, t);
            },
            sortable: true,
            headerClassName: '--secondary',

            className: 'pre-wrap-cell',
            headerStyle: {
                cursor: 'pointer',
            },
        },
        {
            Header: t ? t('socialPostMetrics.columns.caption') : 'Caption',
            accessor: 'content',
            sortable: false,
            headerClassName: '--secondary',
            width: 200,
        },
        {
            Header: t ? t('socialPostMetrics.columns.status') : 'Status',
            accessor: 'analyzed',
            // eslint-disable-next-line react/prop-types,react/no-multi-comp
            Cell: ({ value }) => {
                const icon = value
                    ? 'fa fa-fw fa-check-circle -color-tx-green'
                    : 'fa fa-fw fa-minus-circle';
                const analyzedText = t
                    ? t('socialPostMetrics.columns.analyzed')
                    : 'Analyzed';
                const notAnalyzedText = t
                    ? t('socialPostMetrics.columns.notAnalyzed')
                    : 'Not Analyzed';

                return (
                    <Tooltip
                        data-test="status-tooltip"
                        text={value ? analyzedText : notAnalyzedText}
                        position="bottom"
                    >
                        {isV2 && (
                            <Flex justify="center" align="center">
                                <div
                                    style={{
                                        marginRight: 8,
                                        width: 8,
                                        height: 8,
                                        borderRadius: '50%',
                                        backgroundColor: value
                                            ? '#4CAF50'
                                            : '#ccc',
                                    }}
                                    data-test="post-status"
                                />
                                <Typography.Text>
                                    {value ? analyzedText : notAnalyzedText}
                                </Typography.Text>
                            </Flex>
                        )}
                        {!isV2 && <i className={icon} aria-hidden="true" />}
                    </Tooltip>
                );
            },
            sortable: false,
            headerClassName: '--secondary',
            style: {
                overflow: 'visible',
            },
        },
        {
            Header: getHeader(NAME_ACCESSOR_COLUMNS.IMPRESSIONS),
            accessor: 'impressions',
            Cell: ({ value }: { value: number }) => format.decimal(value),
            textAlign: 'center',
            sortMethod: sorter.number,
            isSecondary: true,
            headerClassName: '--primary',
            Footer: () => format.decimal(totals.impressionsTotal),
            sortable: true,
            width: 100,
            headerStyle: {
                cursor: 'pointer',
            },
        },
        {
            Header: getHeader(NAME_ACCESSOR_COLUMNS.VIDEO_VIEWS),
            accessor: 'videoViews',
            Cell: ({ value }: { value: number }) => format.decimal(value),
            textAlign: 'center',
            sortMethod: sorter.number,
            headerClassName: '--primary',
            Footer: () => format.decimal(totals.videoViewsTotal),
            sortable: true,
            width: 100,
            headerStyle: {
                cursor: 'pointer',
            },
        },
        {
            Header: getHeader(NAME_ACCESSOR_COLUMNS.ENGAGEMENTS),
            accessor: 'engagements',
            Cell: ({ value }: { value: number }) => format.decimal(value),
            textAlign: 'center',
            sortMethod: sorter.number,
            width: 100,
            headerClassName: '--primary',
            Footer: () => format.decimal(totals.engagementsTotal),
            sortable: true,
            headerStyle: {
                cursor: 'pointer',
            },
        },
        {
            Header: getHeader(NAME_ACCESSOR_COLUMNS.FMV),
            accessor: 'fullMediaValue',
            Cell: ({ value }: { value: number }) =>
                format.currency(value, { currency }),
            textAlign: 'center',
            sortMethod: sorter.number,
            headerClassName: '--primary',
            sortable: true,
            width: 95,
            Footer: () => format.decimal(totals.fmvTotal, true),
            headerStyle: {
                cursor: 'pointer',
            },
        },
        !isV2 && {
            Header: t ? t('socialPostMetrics.columns.link') : 'Link',
            accessor: 'url',
            // eslint-disable-next-line react/no-multi-comp,react/display-name
            Cell: ({ value }: { value: string }) => {
                return (
                    <a
                        href={value}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="gds-button gds-button--sm gds-button--link"
                        data-test="post-link"
                    >
                        <i className="fa fa-fw fa-link" aria-hidden="true" />
                    </a>
                );
            },
            textAlign: 'center',
            headerClassName: '--secondary',
            width: 70,
            sortable: false,
        },
        {
            Header: t ? t('socialPostMetrics.columns.campaigns') : 'Campaigns',
            accessor: 'campaigns',
            width: 122,
            // eslint-disable-next-line react/no-multi-comp,react/prop-types
            Cell: row => {
                const { original, value } = row;
                const campaigns = value ?? [];

                if (!campaigns.length) {
                    return null;
                }

                return (
                    <>
                        {!isV2 && (
                            <ExpandableCampaignTags
                                postId={original.id}
                                onRemoveCampaign={original.removeCampaign}
                                campaigns={campaigns}
                            />
                        )}
                        {isV2 && (
                            <ExpandableCampaignTagsV2
                                postId={original.id}
                                onRemoveCampaign={original.removeCampaign}
                                campaigns={campaigns}
                            />
                        )}
                    </>
                );
            },
            className: 'post-campaigns-cell',
            headerClassName: '--secondary',
            sortable: false,
            style: {
                width: '100%',
                overflow: 'visible',
            },
        },
    ].filter(Boolean) as any[];
};

const getMediaTypeNameById = (
    mediaTypeId: number,
    t?: (key: string) => string,
) => {
    const mediaTypes = t
        ? [
              t('common:filters.mediaType.image'),
              t('common:filters.mediaType.video'),
              t('common:filters.mediaType.text'),
          ]
        : ['Image', 'Video', 'Text'];
    return mediaTypes[mediaTypeId - 1];
};
